import React, { useState } from 'react';
import { Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import useAuthStore from '../../stores/authStore';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription } from '../../components/ui/alert';
import LoadingSpinner from '../../components/LoadingSpinner';
import { Hotel, Shield, UserPlus, ArrowLeft } from 'lucide-react';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const { login, isAuthenticated, isAdmin, isLoading, error } = useAuthStore();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Check for URL parameters (confirmation status)
  const urlParams = new URLSearchParams(location.search);
  const confirmed = urlParams.get('confirmed');
  const urlError = urlParams.get('error');

  // Redirect if already authenticated
  if (isAuthenticated) {
    const from = location.state?.from?.pathname || (isAdmin() ? '/admin' : '/app');
    return <Navigate to={from} replace />;
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) return;

    const result = await login(email, password, false); // Regular user login only
    
    if (result.success) {
      // For now, redirect all new tenant users to onboarding
      // Later we can add logic to check if they have brands/hotels set up
      const user = result.user;
      
      // Determine redirect based on user type and setup status
      console.log('🔍 Login redirect logic - User data:', user);
      
      if (user?.roles?.some(role => role.role === 'owner')) {
        // Tenant owner - check setup status
        const hasCompleteSetup = user.roles.some(role => 
          role.role === 'owner' && role.brand_id && role.hotel_id
        );
        
        console.log('🔍 Owner role detected:', {
          roles: user.roles,
          hasCompleteSetup,
          ownerRoles: user.roles.filter(role => role.role === 'owner')
        });
        
        if (hasCompleteSetup) {
          // User has complete setup, go to dashboard
          console.log('✅ Redirecting to dashboard (complete setup)');
          navigate('/app/dashboard');
        } else {
          // User needs setup, go to onboarding
          console.log('✅ Redirecting to onboarding (needs setup)');
          navigate('/app/onboarding');
        }
      } else if (user?.roles?.some(role => role.role === 'admin')) {
        // System admin, go to admin panel
        console.log('✅ Redirecting to admin panel');
        navigate('/admin');
      } else {
        // Regular user or staff member
        console.log('✅ Redirecting to dashboard (regular user)');
        const from = location.state?.from?.pathname || '/app/dashboard';
        navigate(from);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="flex items-center justify-center w-16 h-16 bg-primary-600 rounded-full">
              <Hotel className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl">Sign In</CardTitle>
          <CardDescription>
            Sign in to your Think Robin account
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {confirmed && (
            <Alert className="mb-4 border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">
                ✅ Email confirmed successfully! You can now sign in.
              </AlertDescription>
            </Alert>
          )}
          
          {urlError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {urlError === 'invalid_confirmation' && 'Invalid confirmation link.'}
                {urlError === 'confirmation_failed' && 'Email confirmation failed.'}
                {urlError === 'confirmation_error' && 'An error occurred during confirmation.'}
                {!['invalid_confirmation', 'confirmation_failed', 'confirmation_error'].includes(urlError) && 'An error occurred.'}
              </AlertDescription>
            </Alert>
          )}
          
          {location.state?.message && (
            <Alert className="mb-4 border-green-200 bg-green-50">
              <AlertDescription className="text-green-800">
                {location.state.message}
              </AlertDescription>
            </Alert>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoading}
                required
              />
            </div>

            {error && (
              <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">
                {error}
              </div>
            )}

            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || !email || !password}
            >
              {isLoading ? <LoadingSpinner size="small" text="" /> : 'Sign In'}
            </Button>
          </form>

          <div className="mt-6 space-y-4">
            <div className="text-center">
              <Link 
                to="/auth/forgot-password" 
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                Forgot your password?
              </Link>
            </div>
            
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Don't have an account?{' '}
                <Link 
                  to="/register" 
                  className="text-primary hover:text-primary/80 font-medium inline-flex items-center"
                >
                  <UserPlus className="w-4 h-4 mr-1" />
                  Create Account
                </Link>
              </p>
            </div>
            
            <div className="pt-4 border-t border-gray-200 space-y-2">
              <Link 
                to="/admin/login" 
                className="flex items-center justify-center space-x-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <Shield className="w-4 h-4" />
                <span>Admin Console Login</span>
              </Link>
              
              <Link 
                to="/" 
                className="flex items-center justify-center space-x-2 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Home</span>
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
